import { useAuth } from 'oidc-react';
import React, { useEffect, useState } from 'react';
import SHRMMessage from '../../common/components/SHRMMessage';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { IAccount } from '../../models/Account/IAccount';
import { IAddress } from '../../models/Account/IAddress';
import { IAuthChange } from '../../models/Account/IAuthChange';
import { IContactEdit } from '../../models/Account/IContactEdit';
import { IPhone } from '../../models/Account/IPhone';
import { setAccount, setContactConfig, setContactEdit } from '../../redux/accountSlice';
import { RootState } from '../../redux/store';
import {
  getAccount,
  getAllEntries,
  getContactConfigData,
  getContactEditData,
  getStatesForCountry,
  handleErrorResponse,
  saveAddress,
  saveEmailPassword,
  saveHrMagazineSubscription,
  savePhone,
  updatePreferences,
} from '../../services';
import styles from './AccountPage.module.css';
import AddressesSection from './components/AddressesSection';
import AuthenticationSection from './components/AuthenticationSection';
import AuthenticationModal from './components/AuthenticationSection/components/AuthenticationModal';
import CertificationSection from './components/CertificationSection';
import CredentialsSection from './components/CredentialsSection';
import MembershipSection from './components/MembershipSection';
import PaymentMethodsSection from './components/PaymentMethodsSection';
import PhoneNumbersSection from './components/PhoneNumbersSection';
import MagazineDeliverySection from './components/MagazineDeliverySection';
import LoadingSpinner from '../../common/components/LoadingSpinner';
import { IEntry } from '../../models/Account/IEntry';
import { ICommPreference } from '../../models/Account/ICommPreference';
import CommunicationPreferencesSection from './components/CommunicationPreferencesSection';

const AccountPage = () => {
  const profileContainer = useAppSelector(
    (state: RootState) => state.profileReducer.profileContainer
  );

  const accountContainer = useAppSelector((state: RootState) => state.accountReducer.accountData);
  const membership = useAppSelector((state: RootState) => state.accountReducer.membership);
  const userStatus = useAppSelector((state: RootState) => state.accountReducer.userStatus);

  const [accountData, setAccountData] = useState<IAccount>(accountContainer);
  const [accountAllData, setAccountAllData] = useState<IEntry>();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isChangeEmailModal, setIsChangeEmailModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [popupMesage, setPopupMessage] = useState('');
  const [popupMessageColor, setPopupMessageColor] = useState('');
  const [showPopupMessage, setShowPopupMessage] = useState(false);

  const dispatch = useAppDispatch();

  const auth = window.name === 'true' ? null : useAuth();

  const toggleAuthModal = (isEmailModal: boolean = true) => {
    setIsChangeEmailModal(isEmailModal);
    setShowAuthModal(!showAuthModal);
  };

  const saveEmailPasswordChange = (authChange: IAuthChange) => {
    authChange.id = accountContainer.userEmail;
    return saveEmailPassword(authChange)
      .then((response: any) => {
        return getAccount(auth?.userData?.profile?.SSOUUID as string);
      })
      .then((_response: any) => {
        setAccountData(_response.data);
        dispatch(setAccount(_response.data));
        // close modal
        toggleAuthModal();
        window.scrollTo(0, 0);
        setPopupMessage(
          isChangeEmailModal
            ? '✔︎ Your email has been changed!'
            : '✔︎ Your password has been changed'
        );
        setPopupMessageColor('var(--shrmGreen)');
        setShowPopupMessage(true);
        accountData.userEmail = authChange.email;
      })
      .finally(() => {
        setTimeout(() => {
          setShowPopupMessage(false);
        }, 3000);
      });
  };

  const addAddress = (newAddress: IAddress) => {
    return saveAddress(auth.userData?.profile.SSOUUID as string, newAddress)
      .then((response: any) => {
        return getAccount(auth?.userData?.profile?.SSOUUID as string);
      })
      .then((_response: any) => {
        setAccountData(_response.data);
        dispatch(setAccount(_response.data));
        window.scrollTo(0, 0);
        setPopupMessage('✔︎ New address saved.');
        setPopupMessageColor('var(--shrmGreen)');
        setShowPopupMessage(true);
      })
      .finally(() => {
        setTimeout(() => {
          setShowPopupMessage(false);
        }, 3000);
      });
  };

  const updateAddress = (address: IAddress) => {
    return saveAddress(auth.userData?.profile.SSOUUID as string, address)
      .then((response: any) => {
        return getAccount(auth?.userData?.profile?.SSOUUID as string);
      })
      .then((_response: any) => {
        setAccountData(_response.data);
        dispatch(setAccount(_response.data));
        window.scrollTo(0, 0);
        setPopupMessage('✔︎ The address is updated.');
        setPopupMessageColor('var(--shrmGreen)');
        setShowPopupMessage(true);
      })
      .finally(() => {
        setTimeout(() => {
          setShowPopupMessage(false);
        }, 3000);
      });
  };

  const deleteAddress = (address: IAddress) => {
    // const newPhoneList = phoneNumbers.filter(_phone => _phone.phoneId !== phone.phoneId);
    // setPhoneNumbers(newPhoneList);
  };

  const addPhone = (newPhone: IPhone) => {
    return savePhone(profileContainer?.profileModel?.masterCustomerId, newPhone)
      .then((response: any) => {
        return getAccount(auth?.userData?.profile?.SSOUUID as string);
      })
      .then((response: any) => {
        setAccountData(response.data);
        dispatch(setAccount(response.data));
        window.scrollTo(0, 0);
        setPopupMessage('✔︎ Phone number updated.');
        setPopupMessageColor('var(--shrmGreen)');
        setShowPopupMessage(true);
      })
      .finally(() => {
        setTimeout(() => {
          setShowPopupMessage(false);
        }, 3000);
      });
  };

  const updatePhone = (phone: IPhone) => {
    return savePhone(profileContainer?.profileModel?.masterCustomerId, phone)
      .then((response: any) => {
        return getAccount(auth?.userData?.profile?.SSOUUID as string);
      })
      .then((response: any) => {
        setAccountData(response.data);
        dispatch(setAccount(response.data));
        window.scrollTo(0, 0);
        setPopupMessage('✔︎ Phone number updated.');
        setPopupMessageColor('var(--shrmGreen)');
        setShowPopupMessage(true);
      })
      .finally(() => {
        setTimeout(() => {
          setShowPopupMessage(false);
        }, 3000);
      });
  };

  const handleHrMagazineSubscriptionTypeSave = (hrMagazineSubscriptionType: number) => {
    window.scrollTo(0, 0);
    setIsLoading(true);
    saveHrMagazineSubscription(
      profileContainer?.profileModel?.masterCustomerId,
      hrMagazineSubscriptionType
    )
      .then((response: any) => {
        return getAccount(auth?.userData?.profile?.SSOUUID as string);
      })
      .then((response: any) => {
        setAccountData(response.data);
        dispatch(setAccount(response.data));
        window.scrollTo(0, 0);
        setPopupMessage('✔︎ Your HR magazine delivery settings have been updated.');
        setPopupMessageColor('var(--shrmGreen)');
        setShowPopupMessage(true);
      })
      .catch((_error: any) => {
        const message = handleErrorResponse(_error);
        setPopupMessage(message);
        setPopupMessageColor('var(--shrmRed)');
        setShowPopupMessage(true);
      })
      .finally(() => {
        setIsLoading(false);
        setTimeout(() => {
          setShowPopupMessage(false);
        }, 3000);
      });
  };

  const handleCommunicationPreferencesSave = (commPreference: ICommPreference) => {
    window.scrollTo(0, 0);
    setIsLoading(true);
    updatePreferences(profileContainer?.profileModel?.masterCustomerId, commPreference)
      .then((response: any) => {
        return getAccount(auth?.userData?.profile?.SSOUUID as string);
      })
      .then((response: any) => {
        setAccountData(response.data);
        dispatch(setAccount(response.data));
        window.scrollTo(0, 0);
        setPopupMessage('✔︎ Your account settings have been updated.');
        setPopupMessageColor('var(--shrmGreen)');
        setShowPopupMessage(true);
      })
      .catch((_error: any) => {
        const message = handleErrorResponse(_error);
        setPopupMessage(message);
        setPopupMessageColor('var(--shrmRed)');
        setShowPopupMessage(true);
      })
      .finally(() => {
        setIsLoading(false);
        setTimeout(() => {
          setShowPopupMessage(false);
        }, 3000);
      });
  };

  const deletePhone = (phone: IPhone) => {
    // const newPhoneList = phoneNumbers.filter(_phone => _phone.phoneId !== phone.phoneId);
    // setPhoneNumbers(newPhoneList);
  };

  const changeCountry = (countryCode: string) => {
    return getContactConfigData(countryCode);
  };

  const fetchStates = (countryCode: string) => {
    return getStatesForCountry(countryCode);
  };

  useEffect(() => {
    if (auth && auth.isLoading) {
      return;
    }
    setIsLoading(true);
    getAllEntries(
      auth.userData?.profile.SSOUUID as string,
      auth.userData?.profile.SSOUUID as string
    )
      .then((response: any) => {
        const accountentry = response.data as IEntry;
        setAccountAllData(accountentry);
        return getAccount(auth.userData?.profile.SSOUUID as string);
      })
      .then((response: any) => {
        setAccountData(response.data);
        return getContactEditData();
      })
      .then((response: any) => {
        dispatch(setContactEdit(response.data));
      })
      .catch((_error: any) => {
        handleErrorResponse(_error);
      })
      .finally(() => {
        setIsLoading(false);
      });

    // } else {
    //   navigate('../signout', { replace: true });
    //   alert('Invalid token! You are signed out!');
    // }
  }, [auth?.isLoading]);

  return (
    <React.Fragment>
      <SHRMMessage color={popupMessageColor} text={popupMesage} showMessage={showPopupMessage} />
      <AuthenticationModal
        modalShow={showAuthModal}
        modalIsChangeEmail={isChangeEmailModal}
        modalExistingEmail={accountData.userEmail}
        modalClose={toggleAuthModal}
        modalSave={saveEmailPasswordChange}
      />
      <div className={styles.container}>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <AuthenticationSection
              authEmail={accountData.userEmail}
              toggleAuthModal={toggleAuthModal}
            />
            <PaymentMethodsSection address={accountData.manageCreditCardsUrl} />
            <MembershipSection
              accountData={accountData}
              ssoUUId={auth.userData?.profile?.SSOUUID as string}
              memberhip={membership}
              userStatus={userStatus}
            />
            <CertificationSection data={accountAllData} />
            <CredentialsSection
              credentials={accountData.specialtyCredentials}
              qualifications={accountData.customerQualifications}
            />
            <PhoneNumbersSection
              phoneNumbers={accountData.phones}
              onAddPhone={addPhone}
              onUpdatePhone={updatePhone}
              onDeletePhone={deletePhone}
              onChangeCountry={changeCountry}
            />
            <AddressesSection
              addresses={accountData.addresses}
              onAddAddress={addAddress}
              onUpdateAddress={updateAddress}
              onDeleteAddress={deleteAddress}
              onChangeCountry={changeCountry}
              fetchStates={fetchStates}
            />
            {accountData.hrMagazineSubscriptionEnabled && (
              <MagazineDeliverySection
                hrMagazineSubscriptionType={accountData.hrMagazineSubscriptionType}
                handleSave={handleHrMagazineSubscriptionTypeSave}
              />
            )}

            <CommunicationPreferencesSection
              commPreference={accountData.commPreferenceModel}
              handleSave={handleCommunicationPreferencesSave}
            />
          </>
        )}
      </div>
    </React.Fragment>
  );
};

export default AccountPage;
